import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Bell, BellOff } from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';

interface SaveSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, enableAlerts: boolean) => void;
  currentQuery: string;
  resultCount: number;
}

const SaveSearchModal: React.FC<SaveSearchModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentQuery,
  resultCount
}) => {
  const [searchName, setSearchName] = useState('');
  const [enableAlerts, setEnableAlerts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!searchName.trim()) return;
    
    setIsSaving(true);
    try {
      await onSave(searchName.trim(), enableAlerts);
      setSearchName('');
      setEnableAlerts(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSearchName('');
    setEnableAlerts(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 w-full max-w-md mx-4"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center">
              <Save className="w-5 h-5 mr-2 text-primary-500" />
              Save Company Search
            </h3>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Search Preview */}
            <div className="p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Search Query:</p>
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                {currentQuery || 'All companies'}
              </p>
              <p className="text-xs text-neutral-500">
                {resultCount.toLocaleString()} companies found
              </p>
            </div>
            
            {/* Search Name Input */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Search Name
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="e.g., AI Startups in California"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                autoFocus
              />
            </div>
            
            {/* Alert Settings */}
            <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
              <div className="flex items-center space-x-3">
                {enableAlerts ? (
                  <Bell className="w-5 h-5 text-primary-500" />
                ) : (
                  <BellOff className="w-5 h-5 text-neutral-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Enable Alerts
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    Get notified when new companies match this search
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEnableAlerts(!enableAlerts)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  enableAlerts 
                    ? 'bg-primary-500' 
                    : 'bg-neutral-300 dark:bg-neutral-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enableAlerts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-neutral-200 dark:border-neutral-700">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!searchName.trim() || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Search'}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SaveSearchModal; 